import React from 'react';

const DOMAIN_DICTIONARY = [
  { prefix: 'domain_serpro_ccir_', label: 'CCIR' },
  { prefix: 'domain_serpro_cnd_', label: 'CND' },
  { prefix: 'domain_serpro_', label: 'ENTIDADES' },
  { prefix: 'domain_car_', label: 'CAR' },
  { prefix: 'domain_sigef_', label: 'SIGEF' },
  { prefix: 'domain_sncr_', label: 'SNCR' },
  { prefix: 'domain_cafir_', label: 'CAFIR' },
  { prefix: 'domain_legal_', label: 'JURÍDICO' },
];

function getFriendlyDomain(rawValue) {
  const value = String(rawValue || '').toLowerCase();
  for (const item of DOMAIN_DICTIONARY) {
    if (value.startsWith(item.prefix)) return item.label;
  }
  if (value.startsWith('car')) return 'CAR';
  if (value.startsWith('sigef')) return 'SIGEF';
  if (value.startsWith('sncr')) return 'SNCR';
  if (value.startsWith('cafir')) return 'CAFIR';
  if (value.startsWith('ccir')) return 'CCIR';
  if (value.startsWith('cnd')) return 'CND';
  if (value.startsWith('legal')) return 'JURÍDICO';
  if (value.startsWith('serpro')) return 'ENTIDADES';
  return 'DOMÍNIO';
}

function parseProcessedEntry(entry) {
  const raw = String(entry || '').trim();
  if (!raw) return null;

  const parts = raw.split(/\s*-\s*/);
  if (parts.length >= 2) {
    const domainRaw = parts[0];
    const code = parts.slice(1).join(' - ').trim();
    if (!code) return null;
    return { domain: getFriendlyDomain(domainRaw), code };
  }

  return { domain: getFriendlyDomain(raw), code: raw };
}

function getFriendlyLastStatus(lastStatus, finalStatus) {
  const raw = String(lastStatus || finalStatus || '').toLowerCase();

  if (!raw) return { text: 'Aguardando atualização', tone: 'running' };
  if (raw === 'cache_hit') return { text: 'Dados prontos em cache', tone: 'success' };
  if (raw === 'process_succeeded') return { text: 'Processamento concluído', tone: 'success' };
  if (['process_failed', 'process_timeout', 'process_canceled'].includes(raw)) {
    return { text: 'Falha no processamento', tone: 'failed' };
  }

  const friendlyDomain = getFriendlyDomain(raw);
  if (raw.includes('_failed') || raw.includes('_error')) {
    return { text: `${friendlyDomain} com falha`, tone: 'failed' };
  }
  if (raw.includes('_succeeded') || raw.includes('_ready') || raw.includes('_done')) {
    return { text: `${friendlyDomain} concluído`, tone: 'success' };
  }
  return { text: `${friendlyDomain} em execução`, tone: 'running' };
}

/**
 * Overlay com resumo de progresso de processamento.
 * Exibe status amigável, pendências e domínios/códigos já processados.
 */
export default function ProcessingOverlay({
  visible,
  text = 'Processando dados da propriedade',
  processStatus = null,
  onRetry = null,
}) {
  if (!visible) return null;

  const finalStatus = String(processStatus?.final_status || '').toLowerCase();
  const lastStatus = processStatus?.last_status || processStatus?.sse_last?.status || null;
  const pendingCount = processStatus?.pending_count;
  const domainsProcessed = Array.isArray(processStatus?.domains_processed)
    ? processStatus.domains_processed
    : [];
  const canRetry = Boolean(processStatus?.can_retry) && typeof onRetry === 'function';
  const requestId = processStatus?.request_id || null;

  const dedupedEntries = [];
  const dedupeSet = new Set();
  for (const item of domainsProcessed) {
    const parsed = parseProcessedEntry(item);
    if (!parsed) continue;
    const dedupeKey = `${parsed.domain}|${parsed.code.toLowerCase()}`;
    if (dedupeSet.has(dedupeKey)) continue;
    dedupeSet.add(dedupeKey);
    dedupedEntries.push(parsed);
  }

  const friendlyLastStatus = getFriendlyLastStatus(lastStatus, finalStatus);
  const statusColor =
    friendlyLastStatus.tone === 'success'
      ? '#2E7D32'
      : friendlyLastStatus.tone === 'failed'
        ? '#B71C1C'
        : '#B26A00';

  const statusLabel = (() => {
    if (finalStatus === 'process_succeeded') return 'Concluído';
    if (['process_failed', 'process_timeout', 'process_canceled'].includes(finalStatus))
      return 'Falha no processamento';
    if (typeof pendingCount === 'number')
      return pendingCount > 0 ? 'Processando domínios' : 'Finalizando processamento';
    return 'Processando';
  })();

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 5000,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 10,
          padding: '12px 14px',
          borderRadius: 10,
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
          minWidth: 300,
          maxWidth: 360,
          pointerEvents: 'auto',
          zIndex: 5001,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="30" height="30" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <circle cx="25" cy="25" r="20" fill="none" stroke="#e0e0e0" strokeWidth="6" />
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#F9A825"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray="100"
                strokeDashoffset="75"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 13, color: '#333', opacity: 0.95, fontWeight: 700 }}>
                {text}
              </div>
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: statusColor,
              border: `1px solid ${statusColor}`,
              borderRadius: 999,
              padding: '3px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            {statusLabel}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
          <div
            style={{
              background: '#F8FAFC',
              borderRadius: 8,
              padding: '7px 9px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ fontSize: 11, color: '#64748B', marginBottom: 2 }}>
              Processos Restantes
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
              {typeof pendingCount === 'number' ? pendingCount : '---'}
            </div>
          </div>
        </div>

        <div
          style={{
            background: '#FFFDF5',
            borderRadius: 8,
            padding: '7px 9px',
            border: '1px solid #FDE68A',
          }}
        >
          <div style={{ fontSize: 11, color: '#92400E', marginBottom: 4 }}>
            Domínios/Códigos processados ({dedupedEntries.length})
          </div>
          <div style={{ maxHeight: 74, overflowY: 'auto', fontSize: 12, color: '#78350F' }}>
            {dedupedEntries.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {dedupedEntries.slice(0, 6).map((entry) => (
                  <li key={`${entry.domain}-${entry.code}`}>
                    {entry.domain}: <strong>{entry.code}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <span>Aguardando primeiros eventos de domínio...</span>
            )}
          </div>
        </div>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
        >
          <div style={{ fontSize: 12, color: statusColor, fontWeight: 700 }}>
            Último status: {friendlyLastStatus.text}
          </div>
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              style={{
                border: 'none',
                background: '#0D47A1',
                color: '#fff',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reexecutar atualização
            </button>
          )}
        </div>

        {requestId && <div style={{ fontSize: 10, color: '#94A3B8' }}>req: {requestId}</div>}
      </div>
    </div>
  );
}
